// 現場設備分頁（STN-05 / NODE-04）的資料整理：shadow kinio_station.poe_ports / poe_budget（SPEC #kinio-station-control）。
// 沒報的欄位一律 undefined（UI 顯示「不知道」，不顯示成 0）；MAC 對 Thing 照 STN-05 下方的規則（同家戶、有 device.read 才給名稱與狀態）。
import { can, type VmsDevice } from '../../lib/devices';

export interface PoePort {
  port: number;
  enabled: boolean;
  powered: boolean;
  cls?: number;
  powerW?: number;
  currentMa?: number;
  voltageMv?: number;
  tempC?: number;
  link?: 'up' | 'down';
  speedMbps?: number;
  macs: string[];
  macsMore: boolean;
}

export interface PoeBudget {
  /** 0 = 不知道（SPEC：rated_w 不知道 = 0） */
  ratedW?: number;
  usedW?: number;
  /** 已配置（依 PoE 等級預留；plan SPEC #energy-device poe_budget.allocated_w）；沒報 = undefined */
  allocatedW?: number;
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);

/** shadow reported → 站控 PoE 資料；沒報 poe_ports（桌面 Station、沒有 PSE 的機器）回 null = 不出現分頁（ST-CTL-05）。 */
export function poeOf(reported: Record<string, unknown> | undefined): { ports: PoePort[]; budget?: PoeBudget } | null {
  const st = (reported?.kinio_station ?? null) as Record<string, unknown> | null;
  const raw = st?.poe_ports;
  if (!Array.isArray(raw)) return null;
  const ports: PoePort[] = [];
  for (const x of raw) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const port = num(o.port);
    if (port === undefined) continue;
    const powered = o.powered === true;
    ports.push({
      port,
      enabled: o.enabled !== false,
      powered,
      cls: num(o.class),
      powerW: num(o.power_w),
      currentMa: num(o.current_ma),
      voltageMv: num(o.voltage_mv),
      tempC: num(o.temp_c),
      link: o.link === 'up' || o.link === 'down' ? o.link : undefined,
      speedMbps: num(o.speed_mbps),
      macs: Array.isArray(o.macs) ? o.macs.filter((m): m is string => typeof m === 'string').map((m) => m.toLowerCase()) : [],
      macsMore: o.macs_more === true,
    });
  }
  ports.sort((a, b) => a.port - b.port);
  const b = (st?.poe_budget ?? null) as Record<string, unknown> | null;
  const budget = b && typeof b === 'object' ? { ratedW: num(b.rated_w) || undefined, usedW: num(b.used_w), allocatedW: num(b.allocated_w) } : undefined;
  return { ports, budget };
}

/** ch.list 的一列（nvrd chan_status_json）：只取對 MAC 要用的。 */
export interface ChannelRow {
  ch: number;
  mac?: string;
  protocol?: string;
}

const normMac = (m: string) => m.trim().toLowerCase().replace(/-/g, ':');

/**
 * MAC → 這台 Station 的通道 Thing（`<aid>:ch<N>`，ch.list 的 ch 從 0 起）。
 * 只對同家戶（通道本來就跟 Station 同家戶）且使用者看得到（device.read）的通道；其他 MAC 只顯示 MAC 與「未對到本家戶通道」，
 * 不給名稱、機種、在線——不能讓 Station 變成探測別人設備的工具（SPEC STN-05，Eddy 2026-09-28）。
 */
export function macThings(dev: VmsDevice, channels: ChannelRow[] | undefined): Map<string, VmsDevice> {
  const out = new Map<string, VmsDevice>();
  for (const c of channels ?? []) {
    if (!c.protocol || !c.mac) continue; // 沒啟用的槽位
    const child = dev.children.find((d) => d.homeId === dev.homeId && d.channel === c.ch);
    if (!child) continue;
    const readable = child.access ? can(child, 'device.read') : can(dev, 'device.read');
    if (readable) out.set(normMac(c.mac), child);
  }
  return out;
}
